import { sageBaseHomeApi as api } from './sageBaseHomeApi';

export const addTagTypes = [
    'Care Recipients',
    'Care Instructions',
    'Supply Requests',
    'Visit Schedules',
    'Visits',
    'Team',
    'Person',
    'Team Member',
    'Thanks',
] as const;
const injectedRtkApi = api
    .enhanceEndpoints({
        addTagTypes,
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getCareRecipientDetails: build.query<
                GetCareRecipientDetailsApiResponse,
                GetCareRecipientDetailsApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['Care Recipients', 'Care Instructions'],
            }),
            getCareInstructions: build.query<
                GetCareInstructionsApiResponse,
                GetCareInstructionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/care_instructions`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['Care Recipients', 'Care Instructions'],
            }),
            createCareInstructions: build.mutation<
                CreateCareInstructionsApiResponse,
                CreateCareInstructionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/care_instructions`,
                    method: 'POST',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Care Instructions'],
            }),
            updateCareInstructions: build.mutation<
                UpdateCareInstructionsApiResponse,
                UpdateCareInstructionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/care_instructions`,
                    method: 'PATCH',
                    body: queryArg.careInstructionsRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Care Instructions'],
            }),
            getRequests: build.query<GetRequestsApiResponse, GetRequestsApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/requests`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        lastNDays: queryArg.lastNDays,
                    },
                }),
                providesTags: ['Care Recipients', 'Supply Requests'],
            }),
            createRequests: build.mutation<CreateRequestsApiResponse, CreateRequestsApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/requests`,
                    method: 'PUT',
                    body: queryArg.createRequestsRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Supply Requests'],
            }),
            updateRequest: build.mutation<UpdateRequestApiResponse, UpdateRequestApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/requests/${queryArg.requestRid}`,
                    method: 'PATCH',
                    body: queryArg.updateRequestRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Supply Requests'],
            }),
            deleteRequest: build.mutation<DeleteRequestApiResponse, DeleteRequestApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/requests/${queryArg.requestRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Supply Requests'],
            }),
            fulfillRequests: build.mutation<FulfillRequestsApiResponse, FulfillRequestsApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/requests/fulfill`,
                    method: 'POST',
                    body: queryArg.fulfillRequestsRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Supply Requests'],
            }),
            searchRequests: build.mutation<SearchRequestsApiResponse, SearchRequestsApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/requests/search`,
                    method: 'POST',
                    body: queryArg.searchRequestsRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        lastNDays: queryArg.lastNDays,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Supply Requests'],
            }),
            createVisitSchedule: build.mutation<
                CreateVisitScheduleApiResponse,
                CreateVisitScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visit_schedules`,
                    method: 'POST',
                    body: queryArg.createVisitScheduleRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visit Schedules'],
            }),
            getVisitSchedules: build.query<GetVisitSchedulesApiResponse, GetVisitSchedulesApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visit_schedules`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['Care Recipients', 'Visit Schedules'],
            }),
            getVisitSchedule: build.query<GetVisitScheduleApiResponse, GetVisitScheduleApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visit_schedules/${queryArg.visitScheduleRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['Care Recipients', 'Visit Schedules'],
            }),
            updateVisitSchedule: build.mutation<
                UpdateVisitScheduleApiResponse,
                UpdateVisitScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visit_schedules/${queryArg.visitScheduleRid}`,
                    method: 'PATCH',
                    body: queryArg.updateVisitScheduleRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visit Schedules'],
            }),
            deleteVisitSchedule: build.mutation<
                DeleteVisitScheduleApiResponse,
                DeleteVisitScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visit_schedules/${queryArg.visitScheduleRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visit Schedules'],
            }),
            createVisitScheduleOverride: build.mutation<
                CreateVisitScheduleOverrideApiResponse,
                CreateVisitScheduleOverrideApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visit_schedule_overrides`,
                    method: 'POST',
                    body: queryArg.createVisitScheduleOverrideRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visit Schedules'],
            }),
            updateVisitScheduleOverride: build.mutation<
                UpdateVisitScheduleOverrideApiResponse,
                UpdateVisitScheduleOverrideApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visit_schedule_overrides/${queryArg.visitScheduleOverrideRid}`,
                    method: 'PATCH',
                    body: queryArg.updateVisitScheduleOverrideRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visit Schedules'],
            }),
            deleteVisitScheduleOverride: build.mutation<
                DeleteVisitScheduleOverrideApiResponse,
                DeleteVisitScheduleOverrideApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visit_schedule_overrides/${queryArg.visitScheduleOverrideRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visit Schedules'],
            }),
            getVisitByRid: build.query<GetVisitByRidApiResponse, GetVisitByRidApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits/${queryArg.visitRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['Care Recipients', 'Visits'],
            }),
            updateVisit: build.mutation<UpdateVisitApiResponse, UpdateVisitApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits/${queryArg.visitRid}`,
                    method: 'PATCH',
                    body: queryArg.visitRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visits'],
            }),
            deleteVisit: build.mutation<DeleteVisitApiResponse, DeleteVisitApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits/${queryArg.visitRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visits'],
            }),
            createVisitSummary: build.mutation<
                CreateVisitSummaryApiResponse,
                CreateVisitSummaryApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits/${queryArg.visitRid}/summary`,
                    method: 'PUT',
                    body: queryArg.createVisitSummaryRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visits'],
            }),
            updateVisitSummary: build.mutation<
                UpdateVisitSummaryApiResponse,
                UpdateVisitSummaryApiArg
            >({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits/${queryArg.visitRid}/summary`,
                    method: 'PATCH',
                    body: queryArg.updateVisitSummaryRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visits'],
            }),
            createVisitThank: build.mutation<CreateVisitThankApiResponse, CreateVisitThankApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits/${queryArg.visitRid}/thank`,
                    method: 'PUT',
                    body: queryArg.createVisitThankRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visits'],
            }),
            getVisitThanks: build.query<GetVisitThanksApiResponse, GetVisitThanksApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits/${queryArg.visitRid}/thank`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['Care Recipients', 'Visits'],
            }),
            deleteVisitThank: build.mutation<DeleteVisitThankApiResponse, DeleteVisitThankApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits/${queryArg.visitRid}/thank`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        thankGiverRid: queryArg.thankGiverRid,
                    },
                }),
                invalidatesTags: ['Care Recipients', 'Visits'],
            }),
            getVisits: build.query<GetVisitsApiResponse, GetVisitsApiArg>({
                query: (queryArg) => ({
                    url: `/care_recipients/${queryArg.careRecipientRid}/visits`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        startInstant: queryArg.startInstant,
                        endInstant: queryArg.endInstant,
                    },
                }),
                providesTags: ['Care Recipients', 'Visits'],
            }),
            getPersonDetails: build.query<GetPersonDetailsApiResponse, GetPersonDetailsApiArg>({
                query: (queryArg) => ({
                    url: `/people`,
                    headers: {
                        Authorization: queryArg?.authorization,
                    },
                }),
                providesTags: ['Team', 'Person'],
            }),
            verifyPerson: build.query<VerifyPersonApiResponse, VerifyPersonApiArg>({
                query: (queryArg) => ({
                    url: `/people/verify`,
                    params: {
                        phoneNumber: queryArg.phoneNumber,
                    },
                }),
                providesTags: ['Team', 'Person'],
            }),
            getPersonByRid: build.query<GetPersonByRidApiResponse, GetPersonByRidApiArg>({
                query: (queryArg) => ({
                    url: `/people/${queryArg.personRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['Team', 'Person'],
            }),
            updatePersonDetails: build.mutation<
                UpdatePersonDetailsApiResponse,
                UpdatePersonDetailsApiArg
            >({
                query: (queryArg) => ({
                    url: `/people/${queryArg.personRid}`,
                    method: 'PATCH',
                    body: queryArg.updatePersonRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team', 'Person'],
            }),
            deletePersonAccount: build.mutation<
                DeletePersonAccountApiResponse,
                DeletePersonAccountApiArg
            >({
                query: (queryArg) => ({
                    url: `/people/${queryArg.personRid}`,
                    method: 'DELETE',
                    body: queryArg.personDeleteAccountRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team', 'Person'],
            }),
            createTeam: build.mutation<CreateTeamApiResponse, CreateTeamApiArg>({
                query: (queryArg) => ({
                    url: `/teams`,
                    method: 'PUT',
                    body: queryArg.createTeamRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team'],
            }),
            deleteTeam: build.mutation<DeleteTeamApiResponse, DeleteTeamApiArg>({
                query: (queryArg) => ({
                    url: `/teams/${queryArg.teamRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team'],
            }),
            getTeam: build.query<GetTeamApiResponse, GetTeamApiArg>({
                query: (queryArg) => ({
                    url: `/teams/${queryArg.teamRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['Team'],
            }),
            updateTeam: build.mutation<UpdateTeamApiResponse, UpdateTeamApiArg>({
                query: (queryArg) => ({
                    url: `/teams/${queryArg.teamRid}`,
                    method: 'PATCH',
                    body: queryArg.updateTeamRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team'],
            }),
            updateAddressAccessDetails: build.mutation<
                UpdateAddressAccessDetailsApiResponse,
                UpdateAddressAccessDetailsApiArg
            >({
                query: (queryArg) => ({
                    url: `/teams/${queryArg.teamRid}/address_access_details`,
                    method: 'PATCH',
                    body: queryArg.addressAccessDetailsRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team'],
            }),
            inviteTeamMember: build.mutation<InviteTeamMemberApiResponse, InviteTeamMemberApiArg>({
                query: (queryArg) => ({
                    url: `/team_members`,
                    method: 'POST',
                    body: queryArg.body,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team Member', 'Team'],
            }),
            acceptTeamInvitation: build.mutation<
                AcceptTeamInvitationApiResponse,
                AcceptTeamInvitationApiArg
            >({
                query: (queryArg) => ({
                    url: `/team_members`,
                    method: 'PATCH',
                    body: queryArg.body,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team Member', 'Team'],
            }),
            updateTeamMember: build.mutation<UpdateTeamMemberApiResponse, UpdateTeamMemberApiArg>({
                query: (queryArg) => ({
                    url: `/team_members/${queryArg.teamMemberRid}`,
                    method: 'PATCH',
                    body: queryArg.updateTeamMemberRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team Member', 'Team'],
            }),
            deleteTeamMember: build.mutation<DeleteTeamMemberApiResponse, DeleteTeamMemberApiArg>({
                query: (queryArg) => ({
                    url: `/team_members/${queryArg.teamMemberRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['Team Member', 'Team'],
            }),
            getTeamMemberThanks: build.query<
                GetTeamMemberThanksApiResponse,
                GetTeamMemberThanksApiArg
            >({
                query: (queryArg) => ({
                    url: `/thanks/${queryArg.teamMemberRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        lastNDays: queryArg.lastNDays,
                    },
                }),
                providesTags: ['Thanks'],
            }),
        }),
        overrideExisting: false,
    });
export { injectedRtkApi as sageHomeApi };
export type GetCareRecipientDetailsApiResponse =
    /** status 200 Care Recipient Details Response */ CareRecipient;
export type GetCareRecipientDetailsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
};
export type GetCareInstructionsApiResponse =
    /** status 200 Care Recipient Details Response */ CareRecipient;
export type GetCareInstructionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
};
export type CreateCareInstructionsApiResponse = unknown;
export type CreateCareInstructionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
};
export type UpdateCareInstructionsApiResponse = unknown;
export type UpdateCareInstructionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    careInstructionsRequest: CareInstructions;
};
export type GetRequestsApiResponse = /** status 200 Requests Response */ Request[];
export type GetRequestsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    /** Number of days to look back for requests (between 1 and 365, inclusive) */
    lastNDays?: number;
};
export type CreateRequestsApiResponse = /** status 201 Requests Response */ Request[];
export type CreateRequestsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    createRequestsRequest: {
        requests: (RequestBaseProperties & any)[];
    };
};
export type UpdateRequestApiResponse = /** status 200 Request Response */ Request;
export type UpdateRequestApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    requestRid: string;
    updateRequestRequest: RequestBaseProperties & any;
};
export type DeleteRequestApiResponse = /** status 200 Request Response */ Request;
export type DeleteRequestApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    requestRid: string;
};
export type FulfillRequestsApiResponse = /** status 200 Requests Response */ Request[];
export type FulfillRequestsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    fulfillRequestsRequest: {
        requestRids: string[];
    };
};
export type SearchRequestsApiResponse = /** status 200 Requests Response */ Request[];
export type SearchRequestsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    /** Number of days to look back for requests (between 1 and 365, inclusive) */
    lastNDays?: number;
    searchRequestsRequest: {
        status?: RequestStatus;
        /** Filter by request type */
        requestType?: string;
    };
};
export type CreateVisitScheduleApiResponse =
    /** status 201 Visit Schedule Response */ VisitSchedule;
export type CreateVisitScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    createVisitScheduleRequest: VisitScheduleBaseProperties & any;
};
export type GetVisitSchedulesApiResponse =
    /** status 200 Visit Schedules Response */ VisitSchedule[];
export type GetVisitSchedulesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
};
export type GetVisitScheduleApiResponse = /** status 200 Visit Schedule Response */ VisitSchedule;
export type GetVisitScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitScheduleRid: string;
};
export type UpdateVisitScheduleApiResponse =
    /** status 200 Visit Schedule Response */ VisitSchedule;
export type UpdateVisitScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitScheduleRid: string;
    updateVisitScheduleRequest: VisitScheduleBaseProperties & any;
};
export type DeleteVisitScheduleApiResponse =
    /** status 200 Visit Schedule Response */ VisitSchedule;
export type DeleteVisitScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitScheduleRid: string;
};
export type CreateVisitScheduleOverrideApiResponse = unknown;
export type CreateVisitScheduleOverrideApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    createVisitScheduleOverrideRequest: VisitScheduleBaseProperties & {
        visitScheduleRid?: string;
    } & any;
};
export type UpdateVisitScheduleOverrideApiResponse = unknown;
export type UpdateVisitScheduleOverrideApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitScheduleOverrideRid: string;
    updateVisitScheduleOverrideRequest: VisitScheduleBaseProperties & any;
};
export type DeleteVisitScheduleOverrideApiResponse = unknown;
export type DeleteVisitScheduleOverrideApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitScheduleOverrideRid: string;
};
export type GetVisitByRidApiResponse = /** status 200 Visit Response */ Visit;
export type GetVisitByRidApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitRid: string;
};
export type UpdateVisitApiResponse = /** status 200 Visit Response */ Visit;
export type UpdateVisitApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitRid: string;
    visitRequest: Visit;
};
export type DeleteVisitApiResponse = /** status 200 Visit Response */ Visit;
export type DeleteVisitApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitRid: string;
};
export type CreateVisitSummaryApiResponse = /** status 201 Visit Summary Response */ VisitSummary;
export type CreateVisitSummaryApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitRid: string;
    createVisitSummaryRequest: VisitSummaryBase & any;
};
export type UpdateVisitSummaryApiResponse = /** status 200 Visit Summary Response */ VisitSummary;
export type UpdateVisitSummaryApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitRid: string;
    updateVisitSummaryRequest: VisitSummaryBase & {
        visitSummaryRid?: string;
    } & any;
};
export type CreateVisitThankApiResponse = /** status 201 Visit Thank Response */ VisitThank;
export type CreateVisitThankApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitRid: string;
    createVisitThankRequest: {
        /** The RID of the team member receiving the thank */
        thankReceiverRid: string;
    };
};
export type GetVisitThanksApiResponse = /** status 200 Visit Thanks Response */ VisitThanks;
export type GetVisitThanksApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitRid: string;
};
export type DeleteVisitThankApiResponse = /** status 200 Visit Thank Response */ VisitThank;
export type DeleteVisitThankApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    visitRid: string;
    /** The RID of the team member who gave the thank */
    thankGiverRid: string;
};
export type GetVisitsApiResponse = /** status 200 Visits Response */ Visit[];
export type GetVisitsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careRecipientRid: string;
    /** visit start date represented as instant in epoch milliseconds */
    startInstant: InstantInEpochMsParam;
    /** visit end date represented as instant in epoch milliseconds */
    endInstant: InstantInEpochMsParam;
};
export type GetPersonDetailsApiResponse = /** status 200 Person with Team Response */ {
    person: Person;
    teams: Team[];
};
export type GetPersonDetailsApiArg = {
    authorization?: string;
};
export type VerifyPersonApiResponse = unknown;
export type VerifyPersonApiArg = {
    phoneNumber: string;
};
export type GetPersonByRidApiResponse = /** status 200 Person with Team Response */ {
    person: Person;
    teams: Team[];
};
export type GetPersonByRidApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    personRid: string;
};
export type UpdatePersonDetailsApiResponse = /** status 200 Person with Team Response */ {
    person: Person;
    teams: Team[];
};
export type UpdatePersonDetailsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    personRid: string;
    updatePersonRequest: {
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
        email?: string;
        dateOfBirth?: string;
        preferredName?: string;
        pronouns?: Pronouns;
        isTermsAccepted?: boolean;
    };
};
export type DeletePersonAccountApiResponse = unknown;
export type DeletePersonAccountApiArg = {
    personRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    personDeleteAccountRequest: {
        deleteAccountReason: string;
    };
};
export type CreateTeamApiResponse = /** status 200 Team Response */ {
    team: Team;
};
export type CreateTeamApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createTeamRequest: {
        policyId: string;
        policyProviderName: string;
        careRecipient: BasePerson;
        primaryCaregiver: BasePerson;
        address: Address;
        /** Additional information about accessing the address, such as gate codes, parking instructions, or building entry details. */
        addressAccessDetails?: string;
    };
};
export type DeleteTeamApiResponse = /** status 200 Team Response */ {
    team: Team;
};
export type DeleteTeamApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    teamRid: string;
};
export type GetTeamApiResponse = /** status 200 Team Response */ {
    team: Team;
};
export type GetTeamApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    teamRid: string;
};
export type UpdateTeamApiResponse = /** status 200 Team Response */ {
    team: Team;
};
export type UpdateTeamApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    teamRid: string;
    updateTeamRequest: {
        policyId?: string;
        policyProviderName?: string;
        /** Additional information about accessing the address, such as gate codes, parking instructions, or building entry details. */
        addressAccessDetails?: string;
    };
};
export type UpdateAddressAccessDetailsApiResponse = /** status 200 Team Response */ {
    team: Team;
};
export type UpdateAddressAccessDetailsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    teamRid: string;
    addressAccessDetailsRequest: {
        /** Additional information about accessing the address, such as gate codes, parking instructions, or building entry details. */
        addressAccessDetails: string;
    };
};
export type InviteTeamMemberApiResponse =
    /** status 201 Invited Team Member Response */ TeamMemberWithDetails;
export type InviteTeamMemberApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    body: {
        teamRid: string;
        teamRole: TeamMemberRole;
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        isTeamAdmin: boolean;
    };
};
export type AcceptTeamInvitationApiResponse = /** status 200 Person with Team Response */ {
    person: Person;
    teams: Team[];
};
export type AcceptTeamInvitationApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    body: {
        teamRid: string;
    };
};
export type UpdateTeamMemberApiResponse = /** status 200 Person with Team Response */ {
    person: Person;
    teams: Team[];
};
export type UpdateTeamMemberApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    teamMemberRid: string;
    updateTeamMemberRequest: {
        isAdmin?: boolean;
        teamRole?: TeamMemberRole;
    } & NotificationSettings;
};
export type DeleteTeamMemberApiResponse = /** status 200 Person with Team Response */ {
    person: Person;
    teams: Team[];
};
export type DeleteTeamMemberApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    teamMemberRid: string;
};
export type GetTeamMemberThanksApiResponse = /** status 200 Visit Thanks Response */ VisitThanks;
export type GetTeamMemberThanksApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    teamMemberRid: string;
    /** Number of days to look back for thanks (between 1 and 365, inclusive) */
    lastNDays?: number;
};
export type Pronouns = 'HE_HIM_HIS' | 'SHE_HER_HERS' | 'THEY_THEM_THEIR';
export type Adl =
    | 'APPOINTMENTS'
    | 'BATHING'
    | 'DRESSING'
    | 'FEEDING'
    | 'GROOMING'
    | 'MEDICATIONS'
    | 'PREPARING_MEALS'
    | 'TOILETING'
    | 'TRANSFERRING';
export type AdlDetails = {
    adlRid: string;
    name?: Adl;
    enabled?: boolean;
    notes?: string;
};
export type DayToDay = 'MEAL_PREFERENCES' | 'MOBILITY' | 'ROUTINES_PREFERENCES' | 'THINKING_MEMORY';
export type DayToDayDetails = {
    dayToDayRid: string;
    name?: DayToDay;
    enabled?: boolean;
    notes?: string;
};
export type CareInstructions = {
    adls?: AdlDetails[];
    dayToDays?: DayToDayDetails[];
};
export type CareRecipient = {
    careRecipientRid?: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    dateOfBirth?: string;
    preferredName?: string;
    pronouns?: Pronouns;
    isTermsAccepted?: boolean;
    careInstructions?: CareInstructions;
};
export type RequestType = 'SUPPLY' | 'MAINTENANCE' | 'TRANSPORTATION';
export type RequestStatus = 'NEEDED' | 'COMPLETED';
export type InstantInEpochMs = number;
export type RequestBaseProperties = {
    requestType?: RequestType;
    /** Description of the request */
    requestDescription?: string;
    status?: RequestStatus;
    visitRid?: string;
    fulfilledByTeamMemberRid?: string;
    requestCreationInstant?: InstantInEpochMs;
    requestCompletionInstant?: InstantInEpochMs;
};
export type Request = RequestBaseProperties & {
    requestRid: string;
    careRecipientRid: string;
    /** The team member who created the request */
    requestedByRid?: string;
};
export type VisitScheduleBaseProperties = {
    /** list of one-off changes to this visit schedule, if it's recurring */
    visitScheduleOverrideRids?: any;
    caregiverRid?: string;
    /** Instant in epoch milliseconds representing the start date/time of a visit.
    If provided without a cron expression, this represents a one-time visit.
    If provided with a cron expression, this represents the starting point for the recurring schedule,
    and any visits generated by the cron expression will only be created after this point in time.
    If the value is not provided, an instant will be generated representing the current time at visit schedule
    creation.
     */
    scheduleStartInstant?: InstantInEpochMs;
    /** UNIX cron expression defining a recurring visit schedule. Since a cron expression is relative to a
    particular timezone, the `ianaTimezone` property must also be specified.
     */
    cronExpression?: string;
    /** duration in minutes */
    visitDurationMinutes?: number;
    lastBackfillInstant?: InstantInEpochMs;
    /** The IANA timezone name (e.g., America/New_York) for the care recipient's location.
    A timezone must be specified to correctly schedule future visits using schedules based on cron expressions,
    since cron expressions are declaring a user's local timezone expectations (and are subject to daylight
    savings time and other weirdness).
     */
    ianaTimezone?: string;
    /** Number of minutes before the visit ending when a reminder notification should be sent to the caregiver to log into Sage */
    reminderNotificationMinutesBeforeEnd?: number;
    /** Number of minutes after the visit ends (start time + duration) when a visit should be automatically marked as canceled if it is AWAITING_SUMMARY */
    autoCancelMinutesAfterVisit?: number;
    /** Number of minutes before the visit starting when a notification should be sent to the caregiver */
    notificationMinutesBeforeStart?: number;
};
export type VisitSchedule = VisitScheduleBaseProperties & {
    visitScheduleRid: string;
};
export type VisitStatus =
    | 'SCHEDULED'
    | 'IN_PROGRESS'
    | 'AWAITING_SUMMARY'
    | 'COMPLETED'
    | 'CANCELED';
export type VisitThank = {
    visitRid: string;
    thankReceiverRid: string;
    thankGiverRid: string;
    thankedInstant: InstantInEpochMs;
};
export type VisitThanks = VisitThank[];
export type Mood = 'NOT_GOOD' | 'OKAY' | 'GOOD' | 'GREAT';
export type MoodDescriptor =
    | 'MORE_CONFUSED_THAN_USUAL'
    | 'MORE_TIRED_THAN_USUAL'
    | 'MORE_AGITATED_THAN_USUAL'
    | 'MORE_SAD_THAN_USUAL';
export type CareRecipientObservationEnum =
    | 'SKIN_ISSUE'
    | 'INCREASED_PAIN'
    | 'BEHAVIORAL_ISSUES'
    | 'ABNORMAL_URINE'
    | 'ABNORMAL_BOWEL_MOVEMENTS'
    | 'ATE_LESS_THAN_FIFTY_PERCENT_OF_MEAL'
    | 'NEEDS_MORE_HELP_THAN_USUAL';
export type CareRecipientObservation = {
    observationValue: CareRecipientObservationEnum;
    observationNotes?: string;
};
export type ResidenceObservationEnum =
    | 'INTERIOR_MAINTENANCE_NEEDED'
    | 'EXTERIOR_MAINTENANCE_NEEDED';
export type ResidenceObservation = {
    observationValue: ResidenceObservationEnum;
    observationNotes?: string;
};
export type VisitSummaryBase = {
    mood?: Mood;
    moodDescriptors?: MoodDescriptor[];
    careRecipientObservations?: CareRecipientObservation[];
    residenceObservations?: ResidenceObservation[];
    summaryNotes?: string;
};
export type VisitSummary = VisitSummaryBase & {
    visitSummaryRid?: string;
    visitRid?: string;
    visitSummaryCreatedInstant?: InstantInEpochMs;
    visitSummaryLastUpdatedInstant?: InstantInEpochMs;
} & any;
export type Visit = {
    visitRid?: string;
    visitScheduleRid?: string;
    caregiverRid?: string;
    careRecipientRid?: string;
    visitStatus?: VisitStatus;
    visitStartInstant?: InstantInEpochMs;
    /** duration in minutes */
    visitDurationMinutes?: number;
    thanks?: VisitThanks;
    visitSummary?: VisitSummary;
    notes?: string;
};
export type InstantInEpochMsParam = number;
export type BasePerson = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    dateOfBirth?: string;
    preferredName?: string;
    pronouns?: Pronouns;
};
export type Person = BasePerson & {
    personRid: string;
    careRecipientRid?: string;
    isTermsAccepted?: boolean;
};
export type Address = {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    /** USPS 2-letter US State abbreviation */
    state: string;
    /** US Zip Code (5-digit or ZIP+4) */
    zipcode: string;
};
export type TeamConfigurations = {
    policyProviderName: string;
    policyId: string;
    address: Address;
    /** Additional information about accessing the address, such as gate codes, parking instructions, or building entry details. */
    addressAccessDetails?: string;
    ianaTimezone?: string;
};
export type TeamMemberRole =
    | 'CARE_RECIPIENT'
    | 'COMMUNITY_CAREGIVER'
    | 'LICENSED_CAREGIVER'
    | 'TEAM_ADMIN';
export type NotificationSettings = {
    smsNotificationsEnabled?: boolean;
    emailNotificationsEnabled?: boolean;
    emailMarketingNotificationsEnabled?: boolean;
};
export type TeamMemberStatus = 'INVITED' | 'ACTIVE';
export type TeamMemberDetails = {
    teamMemberRid: string;
    role: TeamMemberRole;
    isTeamAdmin: boolean;
    notificationSettings?: NotificationSettings;
    teamMemberStatus?: TeamMemberStatus;
};
export type TeamMemberWithDetails = Person & TeamMemberDetails;
export type Team = {
    teamRid: string;
    teamConfigurations: TeamConfigurations;
    members: TeamMemberWithDetails[];
};
export const {
    useGetCareRecipientDetailsQuery,
    useLazyGetCareRecipientDetailsQuery,
    useGetCareInstructionsQuery,
    useLazyGetCareInstructionsQuery,
    useCreateCareInstructionsMutation,
    useUpdateCareInstructionsMutation,
    useGetRequestsQuery,
    useLazyGetRequestsQuery,
    useCreateRequestsMutation,
    useUpdateRequestMutation,
    useDeleteRequestMutation,
    useFulfillRequestsMutation,
    useSearchRequestsMutation,
    useCreateVisitScheduleMutation,
    useGetVisitSchedulesQuery,
    useLazyGetVisitSchedulesQuery,
    useGetVisitScheduleQuery,
    useLazyGetVisitScheduleQuery,
    useUpdateVisitScheduleMutation,
    useDeleteVisitScheduleMutation,
    useCreateVisitScheduleOverrideMutation,
    useUpdateVisitScheduleOverrideMutation,
    useDeleteVisitScheduleOverrideMutation,
    useGetVisitByRidQuery,
    useLazyGetVisitByRidQuery,
    useUpdateVisitMutation,
    useDeleteVisitMutation,
    useCreateVisitSummaryMutation,
    useUpdateVisitSummaryMutation,
    useCreateVisitThankMutation,
    useGetVisitThanksQuery,
    useLazyGetVisitThanksQuery,
    useDeleteVisitThankMutation,
    useGetVisitsQuery,
    useLazyGetVisitsQuery,
    useGetPersonDetailsQuery,
    useLazyGetPersonDetailsQuery,
    useVerifyPersonQuery,
    useLazyVerifyPersonQuery,
    useGetPersonByRidQuery,
    useLazyGetPersonByRidQuery,
    useUpdatePersonDetailsMutation,
    useDeletePersonAccountMutation,
    useCreateTeamMutation,
    useDeleteTeamMutation,
    useGetTeamQuery,
    useLazyGetTeamQuery,
    useUpdateTeamMutation,
    useUpdateAddressAccessDetailsMutation,
    useInviteTeamMemberMutation,
    useAcceptTeamInvitationMutation,
    useUpdateTeamMemberMutation,
    useDeleteTeamMemberMutation,
    useGetTeamMemberThanksQuery,
    useLazyGetTeamMemberThanksQuery,
} = injectedRtkApi;
